/**
 * Stage S3 (font half) — LaTeX font package → Google Fonts mapping.
 *
 * Owns the whole font layer: the package→font map, the detection scan, and the Google Fonts
 * import URL that the stylesheet interpolates but does not build.
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.4 (R-049…R-073) and
 *       docs/spec/01-preamble-settings-macros-fonts.md §5.2, §5.3.
 *
 * FIDELITY: this module reproduces the source bug-for-bug (CLAUDE.md §3). In
 * particular a comma-separated `\usepackage{lmodern,helvet}` matches **nothing**,
 * because the whole brace body is used as the lookup key (D-06 / R-020). That is
 * observed oracle behaviour, not an oversight — do not "fix" it.
 */
import type { FontPackageEntry, FontSettings } from './types'

/**
 * R-049…R-073 — `FONT_PACKAGE_MAP`. 25 entries, transcribed verbatim.
 * Key = the lower-cased, trimmed brace body of a `\usepackage`.
 *
 * Kept as a plain object literal, exactly as the source has it, so that
 * prototype-name keys (`constructor`, `__proto__`, `valueOf`, …) resolve the same
 * way they do in the original. Those lookups return a truthy non-entry whose
 * `.type` is `undefined`, so every branch is skipped and no font is applied —
 * identical to a miss, but reached by the same route.
 */
export const FONT_PACKAGE_MAP: Record<string, FontPackageEntry> = {
  // Serif fonts
  // PREAMBLE LINK: \usepackage{merriweather} (preambuleV3.tex:6) — "Définition de la fonte
  // d'impression". READ AND HONOURED, and it is the ONLY \usepackage in the reference preamble
  // that reaches the output: this entry supplies both the --latex-main-font stack and the Google
  // Fonts @import at the top of the generated stylesheet. Editing that preamble line really does
  // change the typeface of every document, which is why it must never be deleted as "unread".
  // /!\ The lookup key is the WHOLE brace body (see detectFonts below), so a comma-separated
  // \usepackage{merriweather,xyz} matches nothing and silently falls back to Source Serif 4.
  merriweather: { font: "'Merriweather', Georgia, serif", import: 'Merriweather:ital,wght@0,300;0,400;0,700;1,400', type: 'serif' }, // R-049
  libertine: { font: "'Libre Baskerville', Georgia, serif", import: 'Libre+Baskerville:ital,wght@0,400;0,700;1,400', type: 'serif' }, // R-050
  charter: { font: "'Charis SIL', Georgia, serif", import: 'Charis+SIL:ital,wght@0,400;0,700;1,400', type: 'serif' }, // R-051
  palatino: { font: "'EB Garamond', Palatino, serif", import: 'EB+Garamond:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-052
  mathpazo: { font: "'EB Garamond', Palatino, serif", import: 'EB+Garamond:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-053
  times: { font: "'Libre Baskerville', Times, serif", import: 'Libre+Baskerville:ital,wght@0,400;0,700;1,400', type: 'serif' }, // R-054
  newtxtext: { font: "'Libre Baskerville', Times, serif", import: 'Libre+Baskerville:ital,wght@0,400;0,700;1,400', type: 'serif' }, // R-055
  lmodern: { font: "'Latin Modern Roman', 'Computer Modern', Georgia, serif", import: 'Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-056
  cochineal: { font: "'Crimson Pro', Georgia, serif", import: 'Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-057
  crimson: { font: "'Crimson Pro', Georgia, serif", import: 'Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-058
  sourceserifpro: { font: "'Source Serif 4', Georgia, serif", import: 'Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-059
  'roboto-slab': { font: "'Roboto Slab', Georgia, serif", import: 'Roboto+Slab:wght@300;400;600;700', type: 'serif' }, // R-060
  alegreya: { font: "'Alegreya', Georgia, serif", import: 'Alegreya:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-061
  spectral: { font: "'Spectral', Georgia, serif", import: 'Spectral:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-062
  cormorant: { font: "'Cormorant Garamond', Georgia, serif", import: 'Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-063
  playfair: { font: "'Playfair Display', Georgia, serif", import: 'Playfair+Display:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-064
  lora: { font: "'Lora', Georgia, serif", import: 'Lora:ital,wght@0,400;0,600;0,700;1,400', type: 'serif' }, // R-065
  // Sans-serif fonts
  helvet: { font: "'Inter', Helvetica, sans-serif", import: 'Inter:wght@400;500;600;700', type: 'sans' }, // R-066
  opensans: { font: "'Open Sans', sans-serif", import: 'Open+Sans:ital,wght@0,400;0,600;0,700;1,400', type: 'sans' }, // R-067
  sourcesanspro: { font: "'Source Sans 3', sans-serif", import: 'Source+Sans+3:ital,wght@0,400;0,600;0,700;1,400', type: 'sans' }, // R-068
  roboto: { font: "'Roboto', sans-serif", import: 'Roboto:ital,wght@0,400;0,500;0,700;1,400', type: 'sans' }, // R-069
  lato: { font: "'Lato', sans-serif", import: 'Lato:ital,wght@0,400;0,700;1,400', type: 'sans' }, // R-070
  // Mono fonts
  inconsolata: { font: "'Inconsolata', monospace", import: 'Inconsolata:wght@400;500;600', type: 'mono' }, // R-071
  sourcecodepro: { font: "'Source Code Pro', monospace", import: 'Source+Code+Pro:wght@400;500;600', type: 'mono' }, // R-072
  firacode: { font: "'Fira Code', monospace", import: 'Fira+Code:wght@400;500;600', type: 'mono' }, // R-073
}

/**
 * §3.4 "Defaults when no serif package matches".
 *
 * NOTE: `DEFAULT_FONT_SETTINGS` in `context.ts` names Merriweather instead. That is
 * the value the *real preamble* happens to select, not the converter's default —
 * see the report accompanying this module. These two constants are the source's.
 */
export const DEFAULT_MAIN_FONT = "'Source Serif 4', 'Computer Modern', Georgia, serif"
export const DEFAULT_MAIN_FONT_IMPORT = 'Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400;1,600'

/** §3.4 — appended when no mono package matched. */
export const FALLBACK_MONO_FONT_IMPORT = 'JetBrains+Mono:wght@400;500'

/** §3.4 — `monoFont` fallback stack. */
export const FALLBACK_MONO_FONT = "'JetBrains Mono', 'Courier New', monospace"

/** The seed object, before any `\usepackage` is examined. */
export function createDefaultFontSettings(): FontSettings {
  return {
    mainFont: DEFAULT_MAIN_FONT,
    mainFontImport: DEFAULT_MAIN_FONT_IMPORT,
  }
}

/**
 * R-020 — `\usepackage[opt]{name}` font detection.
 *
 * Scans the WHOLE cleaned document (preamble *and* body — the source never limits
 * this to the preamble region, so a `\usepackage` after `\begin{document}` counts).
 * For each of the three role slots the LAST match in document order wins.
 *
 * Key insertion order of the returned object mirrors the source's assignment order:
 * `mainFont`/`mainFontImport` always first, then the sans/mono pair whichever role
 * was encountered first. That order is observable in the `@import` URL only through
 * `buildGoogleFontsUrl`, which imposes its own main→sans→mono order — but it is
 * preserved anyway so a JSON dump of the settings byte-matches the source's.
 */
export function detectFonts(latex: string): FontSettings {
  const fonts = createDefaultFontSettings()

  // R-020 — verbatim. Note `[^}]+`: the entire brace body becomes the key,
  // so `\usepackage{amsmath,amssymb}` looks up `"amsmath,amssymb"` and misses (D-06).
  const packageRegex = /\\usepackage(?:\[[^\]]*\])?\{([^}]+)\}/g
  let match: RegExpExecArray | null

  while ((match = packageRegex.exec(latex)) !== null) {
    // Group 1 is mandatory in the pattern, so it always participates on a match.
    const packageName = (match[1] ?? '').toLowerCase().trim()
    const fontConfig = FONT_PACKAGE_MAP[packageName]

    if (fontConfig) {
      if (fontConfig.type === 'serif') {
        fonts.mainFont = fontConfig.font
        fonts.mainFontImport = fontConfig.import
      } else if (fontConfig.type === 'sans') {
        fonts.sansFont = fontConfig.font
        fonts.sansFontImport = fontConfig.import
      } else if (fontConfig.type === 'mono') {
        fonts.monoFont = fontConfig.font
        fonts.monoFontImport = fontConfig.import
      }
    }
  }

  return fonts
}

/**
 * §3.4 "Google Fonts URL construction".
 *
 * Array order is always main → sans → mono; when no mono package matched the
 * literal `JetBrains+Mono:wght@400;500` is appended so the URL always names at
 * least two families. `sansFontImport` is downloaded but no CSS rule ever
 * references `sansFont` (D-13 family).
 */
export function buildGoogleFontsUrl(fonts: FontSettings): string {
  const fontImports = [fonts.mainFontImport]
  if (fonts.sansFontImport) fontImports.push(fonts.sansFontImport)
  if (fonts.monoFontImport) {
    fontImports.push(fonts.monoFontImport)
  } else {
    fontImports.push(FALLBACK_MONO_FONT_IMPORT)
  }
  return `https://fonts.googleapis.com/css2?family=${fontImports.join('&family=')}&display=swap`
}

/** `const monoFont = fonts.monoFont || "'JetBrains Mono', 'Courier New', monospace"`. */
export function resolveMonoFont(fonts: FontSettings): string {
  return fonts.monoFont || FALLBACK_MONO_FONT
}
